import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Banner,
  Card,
  Chip,
  Field,
  Icon,
  Input,
  RoleChip,
  Select,
  Textarea,
  useToast,
} from '@w7/ui'
import { useCreateParent } from '@/api/hooks/create/useCreateParent'
import { useUpdateAccount } from '@/api/hooks/member/useUpdateAccount'
import { useStaffMe } from '@/api/hooks/shared/useStaffMe'
import { canEditDownline } from '@/app/staffAccess'
import { casinoCurrencyRequestFields, fundistCurrencyRequestFields, convertDisplayAmount, parseCurrencyType } from '@/lib/currency'

import { ROLE_LABEL, type Account, type AccountStatus } from '@/data/types'
import {
  CreditSection,
  GamesSection,
  RatesSection,
  SECTIONS,
  ratesSectionFields,
  type SectionCtx,
} from '@/features/create/sections'
import { CreateSubmit, SummaryRail, type ChecklistEntry } from '@/features/create/SummaryRail'
import {
  betLimitErrors,
  commissionError,
  editAvailableCreditForThis,
  editCreditError,
  deriveSummary,
  displayNameError,
  parseNumber,
  parentMaxBet,
  shareError,
  type CreateValues,
} from '@/features/create/summary'
import {
  confirmPasswordErrorOptional,
  passwordChecks,
  passwordErrorOptional,
} from '@/features/create/passwordPolicy'
import '@/features/create/create.css'

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
]

const EDIT_SECTIONS = SECTIONS.map((section) =>
  section.id === 'role'
    ? { ...section, label: 'Identity', fields: ['displayName', 'password', 'confirmPassword'] }
    : section,
)

function valuesFromAccount(account: Account): CreateValues {
  return {
    role: account.role,
    username: account.username,
    displayName: account.displayName || account.username,
    password: '',
    confirmPassword: '',
    comments: account.comments ?? '',
    creditLimit: String(account.creditLimit),
    downlineMax: {},
    share: String(account.share.sportsbook),
    shareCasino: String(account.share.casino),
    fancy: String(account.commission.fancy),
    bookmaker: String(account.commission.bookmaker),
    matchOdds: String(account.commission.matchOdds),
    minBet: account.betLimits.min > 0 ? String(account.betLimits.min) : '100',
    maxBet: account.betLimits.max > 0 ? String(account.betLimits.max) : '',
    games: { ...account.games },
    currencyType: parseCurrencyType(account.currencyType) === 'POINTS' ? 'POINTS' : 'INR',
    casinoCurrencyType: account.casinoCurrencyType === 'AED' ? 'AED' : 'HKD',
    fundistCurrencyType: ['HKD', 'AED', 'USD', 'SGD', 'INR'].includes(
      String(account.fundistCurrencyType || '').toUpperCase(),
    )
      ? (String(account.fundistCurrencyType).toUpperCase() as CreateValues['fundistCurrencyType'])
      : 'HKD',
  }
}

function scrollBehaviour(): ScrollBehavior {
  const reduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  return reduced ? 'auto' : 'smooth'
}

const FIELD_LABEL: Record<string, string> = {
  displayName: 'Display name',
  password: 'Password',
  confirmPassword: 'Confirm password',
  creditLimit: 'Credit limit',
  fancy: 'Fancy commission',
  bookmaker: 'Bookmaker commission',
  matchOdds: 'Match odds commission',
  minBet: 'Minimum bet',
  maxBet: 'Maximum bet',
}

function editFieldLabel(field: string, currency: string): string {
  if (field === 'share') return currency === 'INR' ? 'Member Rate' : 'My share'
  if (field === 'shareCasino')
    return currency === 'INR' ? 'Member Rate (Casino)' : 'My share (Casino)'
  return FIELD_LABEL[field] ?? field
}

/**
 * Edit Member — same geometry as Add to downline (section column + sticky
 * Final review rail), with username/role locked and password optional.
 */
export function EditAccountTab({
  account,
  self,
}: {
  account: Account
  self: Account | undefined
}) {
  const toast = useToast()
  const navigate = useNavigate()
  const parentQuery = useCreateParent()
  const update = useUpdateAccount()
  const staffMe = useStaffMe()
  const allowEditGrant = staffMe.isSuccess && canEditDownline(staffMe.data)

  const [values, setValues] = useState<CreateValues>(() => valuesFromAccount(account))
  const [status, setStatus] = useState<AccountStatus>(
    account.status === 'closed' ? 'closed' : account.status,
  )
  const [answered, setAnswered] = useState<Record<string, boolean>>({})
  const [submitted, setSubmitted] = useState(false)

  const controls = useRef(new Map<string, HTMLElement | null>())
  const registrars = useRef(new Map<string, (el: HTMLElement | null) => void>())

  useEffect(() => {
    setValues(valuesFromAccount(account))
    setStatus(account.status === 'closed' ? 'closed' : account.status)
    setAnswered({})
    setSubmitted(false)
  }, [account])

  const canEdit =
    allowEditGrant &&
    Boolean(self) &&
    account.parentId === self?.id &&
    account.status !== 'closed'
  const isMember = account.role === 'player'
  const accountCcy = parseCurrencyType(account.currencyType)
  const showShareForm = !isMember || accountCcy !== 'INR'

  const availableForThis = useMemo(() => {
    const parent = parentQuery.data
    if (!parent) {
      return editAvailableCreditForThis({
        leftoverInChildCurrency: null,
        childCreditLimit: account.creditLimit,
        childRole: account.role,
      })
    }
    const leftoverChild = convertDisplayAmount(
      parent.availableCredit,
      parseCurrencyType(parent.currencyType),
      parseCurrencyType(account.currencyType),
    )
    // Lab parent *MaxCreditLimit for this child role.
    return editAvailableCreditForThis({
      leftoverInChildCurrency: leftoverChild,
      childCreditLimit: account.creditLimit,
      childRole: account.role,
      parentDownlineMax: parent.downlineMaxCredit,
    })
  }, [
    parentQuery.data,
    account.creditLimit,
    account.currencyType,
    account.role,
  ])

  /** Lab UI: Credit Limit should be ≥ |negative Balance Up| (Take). */
  const balanceUpFloor = account.settlement > 0 ? account.settlement : 0

  /** Parent terms with edit ceiling so Credit / review bars match Add Downline. */
  const parentForForm = useMemo(() => {
    if (!parentQuery.data || !self) return null
    return {
      ...parentQuery.data,
      availableCredit: availableForThis,
      currencyType: account.currencyType,
    }
  }, [parentQuery.data, self, availableForThis, account.currencyType])

  const summary = parentForForm ? deriveSummary(values, parentForForm) : null

  const set = <K extends keyof CreateValues>(key: K, value: CreateValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const answer = (field: string, value: string) => {
    if (value.trim() === '') return
    setAnswered((prev) => (prev[field] ? prev : { ...prev, [field]: true }))
  }

  const register = (field: string) => {
    let fn = registrars.current.get(field)
    if (!fn) {
      fn = (el: HTMLElement | null) => {
        if (el) controls.current.set(field, el)
        else controls.current.delete(field)
      }
      registrars.current.set(field, fn)
    }
    return fn
  }

  const errorFor = (field: string): string | null => {
    const err = errors[field] ?? null
    if (!err) return null
    if (submitted || answered[field]) return err
    return null
  }

  const bets = betLimitErrors(
    parseNumber(values.minBet),
    parseNumber(values.maxBet),
    parentMaxBet(self ?? account),
  )

  const shareBase = self?.share.sportsbook ?? 0
  const shareCasinoBase = self?.share.casino ?? 0
  const commissionBase = self?.commission ?? { fancy: 0, bookmaker: 0, matchOdds: 0 }

  const errors: Record<string, string | null> = {
    displayName: displayNameError(values.displayName),
    password: passwordErrorOptional(values.password),
    confirmPassword: confirmPasswordErrorOptional(values.password, values.confirmPassword),
    creditLimit: editCreditError(
      parseNumber(values.creditLimit),
      availableForThis,
      balanceUpFloor,
      parseCurrencyType(account.currencyType),
    ),
    share: showShareForm
      ? shareError(parseNumber(values.share), shareBase, 'sportsbook', account.currencyType)
      : null,
    shareCasino: showShareForm
      ? shareError(parseNumber(values.shareCasino), shareCasinoBase, 'casino', account.currencyType)
      : null,
    fancy: commissionError(parseNumber(values.fancy), commissionBase.fancy, 'fancy'),
    bookmaker: commissionError(parseNumber(values.bookmaker), commissionBase.bookmaker, 'bookmaker'),
    matchOdds: commissionError(parseNumber(values.matchOdds), commissionBase.matchOdds, 'match odds'),
    minBet: isMember ? bets.min : null,
    maxBet: isMember ? bets.max : null,
  }

  const fieldOrder = [
    'displayName',
    'password',
    'confirmPassword',
    'creditLimit',
    ...(showShareForm ? (['share', 'shareCasino'] as const) : []),
    'fancy',
    'bookmaker',
    'matchOdds',
    ...(isMember ? (['minBet', 'maxBet'] as const) : []),
  ]

  const outstanding = fieldOrder
    .map((field) => {
      const message = errors[field]
      return message ? { field, message } : null
    })
    .filter(Boolean) as { field: string; message: string }[]

  const focusField = (field: string) => {
    const el = controls.current.get(field)
    const section =
      field === 'displayName' || field === 'password' || field === 'confirmPassword'
        ? 'role'
        : field === 'creditLimit'
          ? 'credit'
          : field === 'minBet' || field === 'maxBet'
            ? 'games'
            : 'rates'
    document.getElementById(section)?.scrollIntoView({ behavior: scrollBehaviour(), block: 'start' })
    window.setTimeout(() => {
      el?.focus()
      el?.scrollIntoView({ behavior: scrollBehaviour(), block: 'center' })
    }, 50)
  }

  const goToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: scrollBehaviour(), block: 'start' })
  }

  const submit = async () => {
    setSubmitted(true)
    if (!canEdit) {
      toast.error('Cannot edit', 'You can only edit your direct downline.')
      return
    }
    if (outstanding.length > 0) {
      focusField(outstanding[0]!.field)
      return
    }
    const creditParsed = parseNumber(values.creditLimit)
    if (creditParsed === null) return
    const shareParsed = showShareForm
      ? parseNumber(values.share)
      : 0
    const shareCasinoParsed = showShareForm
      ? parseNumber(values.shareCasino)
      : 0
    if (shareParsed === null || shareCasinoParsed === null) return

    try {
      await update.mutateAsync({
        accountId: account.id,
        displayName: values.displayName.trim(),
        status: status === 'closed' ? account.status : status,
        creditLimit: creditParsed,
        share: shareParsed,
        shareCasino: shareCasinoParsed,
        commission: {
          fancy: parseNumber(values.fancy) ?? 0,
          bookmaker: parseNumber(values.bookmaker) ?? 0,
          matchOdds: parseNumber(values.matchOdds) ?? 0,
        },
        betLimits: {
          min: isMember ? (parseNumber(values.minBet) ?? 0) : account.betLimits.min,
          max: isMember ? (parseNumber(values.maxBet) ?? 0) : account.betLimits.max,
        },
        games: isMember ? values.games : account.games,
        comments: values.comments.trim(),
        password: values.password.trim() || undefined,
        currencyType: account.currencyType,
        // Lab: only CM→TM/member send casinoCurrencyType; mid-level omit.
        ...(self
          ? casinoCurrencyRequestFields(
              self.role,
              account.role,
              values.casinoCurrencyType,
            )
          : {}),
        ...(self
          ? fundistCurrencyRequestFields(
              self.role,
              account.role,
              values.fundistCurrencyType,
            )
          : {}),
      })
      setValues((prev) => ({ ...prev, password: '', confirmPassword: '' }))
      setSubmitted(false)
      setAnswered({})
      toast.success('Saved', `${account.username} was updated.`)
      navigate('/downline')
    } catch (err) {
      toast.error('Could not save', (err as Error).message)
    }
  }

  if (!canEdit) {
    return (
      <Banner tone="warn" title="Edit is only for your direct downline">
        {account.status === 'closed'
          ? 'Closed accounts cannot be edited.'
          : `Only ${account.username}'s direct upline can change limits, rates, or password — the same rule as Give and Take.`}
      </Banner>
    )
  }

  if (!parentForForm || !summary) {
    return (
      <Banner tone="info" title="Loading your credit line">
        Fetching the same caps Add to downline uses so this form stays honest.
      </Banner>
    )
  }

  const ctx: SectionCtx = {
    parent: parentForForm,
    role: account.role,
    roles: [account.role],
    values,
    set,
    answer,
    errorFor,
    register,
    availability: 'idle',
    summary,
    variant: 'edit',
  }

  const visibleSections = EDIT_SECTIONS.filter((section) => !section.memberOnly || isMember)

  const checklist: ChecklistEntry[] = visibleSections.map((section) => {
    const sectionFields =
      section.id === 'credit'
        ? ['creditLimit']
        : section.id === 'role'
          ? ['displayName']
          : section.id === 'rates'
            ? ratesSectionFields(account.role, account.currencyType)
            : section.fields
    const relevant = sectionFields.filter((field) => errors[field] !== undefined)
    const bad = relevant.filter((field) => errors[field])
    const touched = relevant.every((field) => {
      if (field === 'password' || field === 'confirmPassword') return true
      return answered[field]
    })
    if (bad.length === 0) {
      return {
        id: section.id,
        label: section.label,
        state: touched || outstanding.length === 0 ? 'done' : 'todo',
      }
    }
    const shown = submitted || bad.some((field) => answered[field])
    return { id: section.id, label: section.label, state: shown ? 'error' : 'todo' }
  })

  const submitProps = {
    label: update.isPending ? 'Saving…' : 'Save changes',
    busy: update.isPending,
    icon: 'check' as const,
    blockReason:
      outstanding.find((item) => submitted || answered[item.field])?.message ?? null,
  }

  const reviewBanners = (
    <>
      {submitted && outstanding.length > 0 ? (
        <Banner
          tone="error"
          title={`${outstanding.length} ${outstanding.length === 1 ? 'thing needs' : 'things need'} fixing before this can be saved`}
        >
          <ul className="w7-create-errlist">
            {outstanding.map((item) => (
              <li key={item.field}>
                <button type="button" onClick={() => focusField(item.field)}>
                  {editFieldLabel(item.field, parseCurrencyType(account.currencyType))} —{' '}
                  {item.message}
                </button>
              </li>
            ))}
          </ul>
        </Banner>
      ) : null}

      <Banner tone="info" title="Password on save">
        Leave password blank to keep the current one. Typing a new password updates their sign-in
        when hierarchy supports it.
      </Banner>

      {update.isError ? (
        <Banner tone="error" title="Changes were not saved">
          {(update.error as Error)?.message ??
            'The request failed before anything was recorded. Nothing beneath you has changed.'}
        </Banner>
      ) : null}

      {parentQuery.isError ? (
        <Banner tone="warn" title="Credit ceiling may be incomplete">
          Could not refresh your remaining credit line. Saving still uses hierarchy rules on the
          server.
        </Banner>
      ) : null}
    </>
  )

  return (
    <form
      className="w7-create"
      noValidate
      onSubmit={(event) => {
        event.preventDefault()
        void submit()
      }}
    >
      <div className="w7-create-main">
        <Banner tone="info" title="Edit downline account">
          Same layout as Add to downline. Username and level stay locked; credit, share, rates
          {isMember ? ', games and bet limits' : ''} can change. Only direct children can be edited.
        </Banner>

        <EditIdentitySection
          ctx={ctx}
          status={status}
          onStatusChange={setStatus}
          statusLocked={account.status === 'closed'}
        />
        <CreditSection ctx={ctx} />
        <RatesSection ctx={ctx} />
        {isMember ? <GamesSection ctx={ctx} /> : null}
      </div>

      <SummaryRail
        ctx={ctx}
        checklist={checklist}
        onNavigate={goToSection}
        submit={submitProps}
        banner={reviewBanners}
        ready={outstanding.length === 0}
      />

      <div className="w7-create-actionbar">
        <CreateSubmit {...submitProps} />
      </div>
    </form>
  )
}

function EditIdentitySection({
  ctx,
  status,
  onStatusChange,
  statusLocked,
}: {
  ctx: SectionCtx
  status: AccountStatus
  onStatusChange: (next: AccountStatus) => void
  statusLocked: boolean
}) {
  const { role, values, set, answer, errorFor, register } = ctx
  const trimmed = values.username.trim()

  return (
    <section id="role" className="w7-create-section" aria-labelledby="role-h" tabIndex={-1}>
      <h2 id="role-h" className="w7-create-h2">
        Identity
      </h2>

      <Card
        title="Level"
        subtitle="Username and level were set at create and cannot move."
      >
        <div className="w7-row" style={{ gap: 'var(--w7-s3)', alignItems: 'center' }}>
          <RoleChip role={role} label={ROLE_LABEL[role]} />
          <span className="w7-mono">{trimmed}</span>
          <Chip size="sm" tone="muted">
            Locked
          </Chip>
        </div>
      </Card>

      <Card title="Identity" subtitle="How this account is named, found, and signs in.">
        <div className="w7-grid w7-grid-2">
          <Field label="Username" hint="Cannot be changed after create.">
            {({ id }) => <Input id={id} value={trimmed} disabled />}
          </Field>

          <Field
            label="Display name"
            required
            hint="Shown in tables and statements."
            error={errorFor('displayName')}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                ref={register('displayName')}
                aria-describedby={describedBy}
                invalid={invalid}
                autoComplete="off"
                value={values.displayName}
                onChange={(event) => set('displayName', event.target.value)}
                onBlur={(event) => answer('displayName', event.target.value)}
              />
            )}
          </Field>
        </div>

        <div className="w7-grid w7-grid-2" style={{ marginTop: 'var(--w7-s4)' }}>
          <Field label="Status" hint="Active, inactive, or suspended.">
            {({ id }) => (
              <Select
                id={id}
                value={status}
                disabled={statusLocked}
                onChange={(e) => onStatusChange(e.target.value as AccountStatus)}
                options={STATUS_OPTIONS}
              />
            )}
          </Field>
        </div>

        <div className="w7-grid w7-grid-2" style={{ marginTop: 'var(--w7-s4)' }}>
          <Field
            label="New password"
            hint="Leave blank to keep the current password."
            error={errorFor('password')}
          >
            {({ id, describedBy, invalid }) => (
              <>
                <Input
                  id={id}
                  ref={register('password')}
                  type="password"
                  aria-describedby={describedBy}
                  invalid={invalid}
                  autoComplete="new-password"
                  value={values.password}
                  onChange={(event) => set('password', event.target.value)}
                  onBlur={(event) => answer('password', event.target.value)}
                />
                {values.password ? (
                  <ul className="w7-create-pwchecks" aria-label="Password requirements">
                    {passwordChecks(values.password).map((check) => (
                      <li key={check.key} data-passed={check.passed ? 'true' : 'false'}>
                        <Icon name={check.passed ? 'check' : 'close'} size={12} />
                        {check.label}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            )}
          </Field>

          <Field label="Confirm password" error={errorFor('confirmPassword')}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                ref={register('confirmPassword')}
                type="password"
                aria-describedby={describedBy}
                invalid={invalid}
                autoComplete="new-password"
                value={values.confirmPassword}
                onChange={(event) => set('confirmPassword', event.target.value)}
                onBlur={(event) => answer('confirmPassword', event.target.value)}
              />
            )}
          </Field>
        </div>

        <div style={{ marginTop: 'var(--w7-s4)' }}>
          <Field
            label="Comments"
            hint="Free text kept on the account — who introduced them, what was agreed."
          >
            {({ id, describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                placeholder="Optional"
                value={values.comments}
                onChange={(event) => set('comments', event.target.value)}
              />
            )}
          </Field>
        </div>
      </Card>
    </section>
  )
}
